import React from 'react'
import styled, { css, th } from '@xstyled/styled-components'
import { Alert } from '../Alert'
import { Block } from '../block/block'
import { Spinner } from '../spinner/spinner'
import { Text } from '../typography/text'

export const Dropzone = ({
  mimeTypes = [],
  message = 'Drag your files here',
  maxFileSize,
  onFilesAdded,
  loading,
  loadingMessage,
  fileCountLimit
}) => {
  const fileInputRef = React.createRef(null)
  const [errorMessage, setErrorMessage] = React.useState('')
  const [highlight, setHighlight] = React.useState(false)

  function dragOver(e) {
    e.preventDefault()
    setHighlight(true)
  }

  function dragEnter(e) {
    e.preventDefault()
  }

  function dragLeave(e) {
    e.preventDefault()
    setHighlight(false)
  }

  function validateFile(file) {
    if (!mimeTypes.length) {
      console.warn(
        '[DropZone] No mime type list provided, accepting all file types'
      )
      return true
    }
    return mimeTypes.includes(file.type)
  }

  function handleFiles(files) {
    const filesHaveValidMimeType = files.every(file => validateFile(file))
    const individualFileSizeNotOverThreshold = files.every(
      file => file.size <= maxFileSize
    )
    const filesCountAboveThreshold = files.length > fileCountLimit

    if (filesCountAboveThreshold) {
      setErrorMessage(
        `File count limit exceeded. Maximum files you can upload at once is ${fileCountLimit}.`
      )
      return
    }

    if (!individualFileSizeNotOverThreshold) {
      setErrorMessage(
        `File size exceeded. Make sure its below ${humanizeFileSize(
          maxFileSize
        )}`
      )
      return
    }

    if (!filesHaveValidMimeType) {
      setErrorMessage(
        `You can only upload one of the following types: ${mimeTypes
          .map(mimeType => `.${mimeType.split('/')[1]}`)
          .join(',')}`
      )
      return
    }

    onFilesAdded(files)
  }

  function fileDrop(e, eventOrigin) {
    e.preventDefault()

    const files =
      eventOrigin === 'dropEvent' ? e.dataTransfer.files : e.target.files

    if (errorMessage) {
      setErrorMessage(null)
    }

    if (files.length) {
      handleFiles(Array.from(files))
    }
    setHighlight(false)
  }

  function humanizeFileSize(size) {
    if (size === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(size) / Math.log(k))
    return parseFloat((size / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  function handleDropAreaClick() {
    fileInputRef.current.click()
  }

  return (
    <Root>
      {errorMessage && <Alert message={errorMessage} mb={2} />}
      <DropArea
        highlight={highlight}
        onDragOver={dragOver}
        onDragEnter={dragEnter}
        onDragLeave={dragLeave}
        onDrop={e => fileDrop(e, 'dropEvent')}
        onClick={handleDropAreaClick}
      >
        {!loading && (
          <Placeholder align='center'>
            <Text tag='p' fontWeight={500}>
              {message}
            </Text>
            <Text tag='small' color='grey.d2'>
              {`Drag file here, or click to browse ${
                maxFileSize ? `(Max ${humanizeFileSize(maxFileSize)})` : ''
              }`}
            </Text>
            <input
              style={{ display: 'none' }}
              ref={fileInputRef}
              accept={mimeTypes
                .map(mimeType => `.${mimeType.split('/')[1]}`)
                .join(',')}
              className='FileInput'
              type='file'
              multiple={fileCountLimit > 1}
              onChange={fileDrop}
            />
          </Placeholder>
        )}
        {loading && (
          <LoaderContainer justify='center' align='center' gap={11}>
            <Spinner radius={24} />
            <Text tag='p' fontWeight={500}>
              {loadingMessage}
            </Text>
          </LoaderContainer>
        )}
      </DropArea>
    </Root>
  )
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 175px;
  width: 100%;
`

const highlightCSS = css({
  borderColor: th.color('primary.base'),
  backgroundColor: th.color('primary.l3')
})

const DropArea = styled.div`
  display: flex;
  background: ${th.color('white')};
  border-radius: 6px;
  height: 100%;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 2px dashed ${th.color('grey.l2')};

  &:hover {
    ${highlightCSS}
  }

  ${({ highlight }) =>
    highlight &&
    css`
      ${highlightCSS}
    `}
`

const Placeholder = styled(Block)``
const LoaderContainer = styled(Block)``
